import { useState, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

// ── Types ──────────────────────────────────────────────────────────────────────

interface StageEvent {
  stage: number
  status: 'running' | 'complete' | 'skipped' | 'error'
  label?: string
  queries?: number
  breaches?: number
  records?: number
  new_identifiers?: number
  error?: string
}

interface CorrelatedEntity {
  value: string
  type: string
  breach_count: number
  confidence: number
  found_by_stages: string[]
  co_occurring_count: number
  co_occurring_with: string[]
}

interface CorrelationResult {
  seed: string
  seed_type: string
  total_queries: number
  queries_sent: string[]
  total_records: number
  breach_count: number
  breach_names: string[]
  entities: CorrelatedEntity[]
  high_confidence_entities: CorrelatedEntity[]
  correlation_graph: Record<string, string[]>
  stage_summaries: any[]
  attribution_score: number
}

interface PipelineState {
  status: 'idle' | 'running' | 'complete' | 'error'
  stages: Record<number, StageEvent>
  result: CorrelationResult | null
  error: string | null
}

interface Props {
  query: string
  onRerunSearch?: (q: string, forceRefresh: boolean) => Promise<void>
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function getToken() { return localStorage.getItem('auth_token') || '' }

function scoreColor(pct: number) {
  return pct >= 70 ? '#22c55e' : pct >= 50 ? '#eab308' : pct >= 35 ? '#f97316' : '#ef4444'
}

function typeColor(t: string) {
  const m: Record<string, string> = {
    email:    'text-blue-400 bg-blue-950/40 border-blue-800/40',
    phone:    'text-cyan-400 bg-cyan-950/40 border-cyan-800/40',
    username: 'text-teal-400 bg-teal-950/40 border-teal-800/40',
    name:     'text-purple-400 bg-purple-950/40 border-purple-800/40',
    address:  'text-orange-400 bg-orange-950/40 border-orange-800/40',
    ip:       'text-red-400 bg-red-950/40 border-red-800/40',
    password: 'text-rose-400 bg-rose-950/40 border-rose-800/40',
  }
  return m[t] ?? 'text-gray-400 bg-gray-800/40 border-gray-700/40'
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StageRow({ n, event }: { n: number; event?: StageEvent }) {
  const status = event?.status ?? 'pending'
  const defs = {
    running: { ring: 'border-yellow-500', dot: 'bg-yellow-400 animate-pulse', badge: 'text-yellow-400 bg-yellow-950/40 border-yellow-800/40', row: 'bg-yellow-950/8 border border-yellow-900/20' },
    complete: { ring: 'border-green-600', dot: 'bg-green-500', badge: 'text-green-400 bg-green-950/30 border-green-800/30', row: 'border border-green-900/10' },
    skipped:  { ring: 'border-gray-700', dot: 'bg-gray-700', badge: 'text-gray-600 bg-gray-900/20 border-gray-700/30', row: 'border border-transparent' },
    error:    { ring: 'border-red-600', dot: 'bg-red-500', badge: 'text-red-400 bg-red-950/30 border-red-800/30', row: 'bg-red-950/8 border border-red-900/20' },
    pending:  { ring: 'border-gray-800', dot: 'bg-gray-800', badge: 'text-gray-700 border-gray-800/30', row: 'border border-transparent' },
  }[status]

  const labels: Record<number, string> = {
    1: 'Seed + Variations Batch',
    2: 'Discovered Identifier Pivot',
    3: 'Exact-Search Name Correlation',
  }

  return (
    <div className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${defs.row}`}>
      <div className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${defs.ring}`}>
        {status === 'running'
          ? <div className={`w-2 h-2 rounded-full ${defs.dot}`} />
          : <span className="font-mono text-[9px] font-bold text-gray-400">{n}</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`font-mono text-xs ${status === 'pending' ? 'text-gray-700' : 'text-gray-300'}`}>
          {event?.label || labels[n]}
        </div>
        {status === 'complete' && event && (
          <div className="font-mono text-[10px] text-gray-600 mt-0.5">
            {event.queries != null && `${event.queries} queries`}
            {event.breaches != null && ` · ${event.breaches} breaches`}
            {event.records  != null && ` · ${event.records} records`}
            {event.new_identifiers != null && event.new_identifiers > 0 && ` · ${event.new_identifiers} new leads`}
          </div>
        )}
        {status === 'error' && event?.error && (
          <div className="font-mono text-[10px] text-red-500 mt-0.5">{event.error}</div>
        )}
      </div>
      <span className={`font-mono text-[9px] font-bold px-1.5 py-0.5 rounded border shrink-0 ${defs.badge}`}>
        {status === 'pending' ? '—' : status.slice(0, 4).toUpperCase()}
      </span>
    </div>
  )
}

function EntityRow({ entity, onInvestigate }: { entity: CorrelatedEntity; onInvestigate: (v: string) => void }) {
  const pct = Math.round(entity.confidence * 100)
  const multistage = entity.found_by_stages.length > 1
  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-black/30 border border-gray-800/50 hover:border-gray-700/60 transition-colors group"
    >
      {/* Confidence bar */}
      <div className="w-1 h-8 rounded-full bg-gray-800 shrink-0 overflow-hidden">
        <div className="w-full rounded-full transition-all duration-700"
          style={{ height: `${pct}%`, background: scoreColor(pct) }} />
      </div>

      {/* Type badge */}
      <span className={`font-mono text-[8px] font-bold px-1.5 py-0.5 rounded border uppercase shrink-0 ${typeColor(entity.type)}`}>
        {entity.type}
      </span>

      {/* Value */}
      <div className="flex-1 min-w-0">
        <div className="text-white font-mono text-xs font-bold truncate">{entity.value}</div>
        <div className="font-mono text-[9px] text-gray-600 mt-0.5">
          {entity.breach_count} breach{entity.breach_count !== 1 ? 'es' : ''}
          {entity.co_occurring_count > 0 && ` · ${entity.co_occurring_count} co-links`}
          {multistage && <span className="text-purple-600"> · multi-stage</span>}
        </div>
      </div>

      {/* Score */}
      <div className="text-right shrink-0">
        <div className="font-mono text-sm font-black" style={{ color: scoreColor(pct) }}>{pct}%</div>
      </div>

      {/* Investigate button */}
      {['email', 'phone', 'username'].includes(entity.type) && (
        <button
          onClick={() => onInvestigate(entity.value)}
          className="opacity-0 group-hover:opacity-100 font-mono text-[9px] px-2 py-1 rounded border border-purple-700/50 text-purple-400 hover:bg-purple-950/40 transition-all shrink-0"
        >
          PIVOT
        </button>
      )}
    </motion.div>
  )
}


// ── Main component ────────────────────────────────────────────────────────────

export function CorrelationEnginePanel({ query, onRerunSearch }: Props) {
  const [state, setState] = useState<PipelineState>({
    status: 'idle', stages: {}, result: null, error: null,
  })
  const [activeView, setActiveView] = useState<'entities' | 'breaches' | 'queries'>('entities')
  const readerRef = useRef<ReadableStreamDefaultReader | null>(null)
  const bufferRef = useRef('')

  const handleEvent = useCallback((evt: any) => {
    switch (evt.event) {
      case 'stage':
        setState(s => ({
          ...s,
          stages: { ...s.stages, [evt.stage]: evt },
        }))
        break
      case 'complete':
        setState(s => ({ ...s, status: 'complete', result: evt.result }))
        break
      case 'error':
        setState(s => ({ ...s, status: 'error', error: evt.message }))
        break
    }
  }, [])

  const start = useCallback(async () => {
    if (readerRef.current) { await readerRef.current.cancel(); readerRef.current = null }
    setState({ status: 'running', stages: {}, result: null, error: null })
    bufferRef.current = ''

    const url = `/api/btra/correlate?query=${encodeURIComponent(query)}&token=${encodeURIComponent(getToken())}`
    try {
      const res = await fetch(url, { headers: { Accept: 'text/event-stream' }, cache: 'no-store' })
      if (!res.ok) {
        const errBody = await res.text()
        setState(s => ({ ...s, status: 'error', error: `HTTP ${res.status}: ${errBody}` }))
        return
      }
      const reader = res.body!.getReader()
      readerRef.current = reader
      const dec = new TextDecoder()

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        bufferRef.current += dec.decode(value, { stream: true })
        const lines = bufferRef.current.split('\n')
        bufferRef.current = lines.pop() ?? ''
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try { handleEvent(JSON.parse(line.slice(6))) } catch { /* malformed */ }
        }
      }
    } catch (err: any) {
      setState(s => ({ ...s, status: 'error', error: err.message ?? 'Connection failed' }))
    }
  }, [query, handleEvent])

  const stop = useCallback(async () => {
    if (readerRef.current) { await readerRef.current.cancel(); readerRef.current = null }
    setState(s => ({ ...s, status: 'idle' }))
  }, [])

  const result    = state.result
  const attrScore = result ? Math.round(result.attribution_score * 100) : 0
  const highConf  = result?.high_confidence_entities ?? []
  const allEnts   = result?.entities ?? []

  const investigate = (val: string) => {
    if (onRerunSearch) onRerunSearch(val, false)
    else window.location.href = `/app/breach?query=${encodeURIComponent(val)}`
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-indigo-900/40 overflow-hidden"
      style={{ background: '#07070f', boxShadow: '0 0 40px rgba(79,70,229,0.06), 0 1px 0 rgba(99,102,241,0.12) inset' }}
    >
      {/* Header */}
      <div className="relative border-b border-indigo-900/40 px-4 py-3"
        style={{ background: 'linear-gradient(135deg, rgba(49,46,129,0.4) 0%, rgba(0,0,0,0.7) 60%)' }}>
        <div className="absolute top-0 left-0 right-0 h-px"
          style={{ background: 'linear-gradient(90deg, transparent, rgba(99,102,241,0.5), transparent)' }} />

        {/* Row 1: title + button */}
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <span className="text-indigo-400 font-mono text-[10px] tracking-widest font-bold uppercase truncate">
            Super-Intelligence Correlator
          </span>
          {state.status === 'running' ? (
            <button onClick={stop}
              className="shrink-0 px-2.5 py-1 bg-red-900/40 hover:bg-red-900/60 border border-red-800/50 text-red-300 font-mono text-[10px] rounded-lg transition-colors">
              STOP
            </button>
          ) : (
            <button onClick={start}
              className="shrink-0 px-3 py-1.5 font-mono text-[10px] font-bold rounded-lg transition-all border border-indigo-600/50 text-indigo-100"
              style={{ background: 'linear-gradient(135deg, rgba(67,56,202,0.7), rgba(79,70,229,0.6))' }}>
              {state.status === 'idle' ? 'RUN CORRELATION' : 'RE-RUN'}
            </button>
          )}
        </div>

        {/* Row 2: badge + query */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-mono text-[9px] text-indigo-600 border border-indigo-900/50 bg-indigo-950/30 px-1.5 py-0.5 rounded-full whitespace-nowrap">
            3-STAGE · BATCH · EXACT-SEARCH
          </span>
          <span className="text-gray-600 font-mono text-[9px] truncate">{query}</span>
        </div>
      </div>

      {/* Pipeline stages */}
      <div className="px-3 py-3 border-b border-indigo-900/20 space-y-1">
        {[1, 2, 3].map(n => (
          <StageRow key={n} n={n} event={state.stages[n]} />
        ))}
      </div>

      {/* Error */}
      {state.error && (
        <div className="px-3 py-3 bg-red-950/30 border-b border-red-900/40">
          <div className="text-red-400 font-mono text-[10px]">{state.error}</div>
        </div>
      )}

      {/* Idle */}
      {state.status === 'idle' && (
        <div className="px-3 py-5 text-center space-y-1">
          <div className="text-gray-700 font-mono text-[10px]">
            Batch multi-line queries · exact-search · cross-breach entity clustering
          </div>
          <div className="text-gray-800 font-mono text-[10px] mt-1">
            10–20 identifier variations · re-queries discovered leads
          </div>
        </div>
      )}

      {/* Results */}
      <AnimatePresence>
        {state.status === 'complete' && result && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>

            {/* Score hero */}
            <div className="px-3 py-3 border-b border-indigo-900/20"
              style={{ background: 'linear-gradient(180deg, rgba(49,46,129,0.12) 0%, transparent 100%)' }}>
              {/* Score bar */}
              <div className="flex items-center gap-2 mb-3">
                <div className="font-mono text-xs text-gray-500">CORRELATION</div>
                <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-1000"
                    style={{ width: `${attrScore}%`, background: scoreColor(attrScore) }} />
                </div>
                <div className="font-mono text-sm font-black shrink-0" style={{ color: scoreColor(attrScore) }}>{attrScore}%</div>
              </div>
              {/* Stats grid */}
              <div className="grid grid-cols-4 gap-1.5">
                {[
                  { v: result.breach_count,    l: 'BREACH' },
                  { v: result.total_records,   l: 'RECORDS' },
                  { v: result.total_queries,   l: 'QUERIES' },
                  { v: highConf.length,        l: 'HI-CONF' },
                ].map(({ v, l }) => (
                  <div key={l} className="text-center bg-black/50 border border-indigo-900/20 rounded-lg py-2">
                    <div className="text-white font-mono text-sm font-black">{v}</div>
                    <div className="text-gray-600 font-mono text-[8px] tracking-widest">{l}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* View tabs */}
            <div className="flex border-b border-indigo-900/20 px-3">
              {[
                { id: 'entities' as const, label: `ENTITIES (${allEnts.length})` },
                { id: 'breaches' as const, label: `BREACHES (${result.breach_count})` },
                { id: 'queries'  as const, label: `QUERIES (${result.total_queries})` },
              ].map(t => (
                <button key={t.id} onClick={() => setActiveView(t.id)}
                  className={`font-mono text-[9px] font-bold tracking-wider px-4 py-3 border-b-2 transition-all whitespace-nowrap ${
                    activeView === t.id
                      ? 'border-indigo-500 text-indigo-300'
                      : 'border-transparent text-gray-600 hover:text-gray-400'
                  }`}>
                  {t.label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="px-3 py-3">

              {/* Entities */}
              {activeView === 'entities' && (
                <div className="space-y-3">
                  {highConf.length > 0 && (
                    <div>
                      <div className="text-indigo-600 font-mono text-[9px] font-bold tracking-widest mb-2 flex items-center gap-2">
                        <div className="w-1 h-2.5 rounded-full bg-indigo-600" />
                        HIGH CONFIDENCE — {highConf.length} entities
                      </div>
                      <div className="space-y-1.5">
                        {highConf.map((e, i) => (
                          <EntityRow key={i} entity={e} onInvestigate={investigate} />
                        ))}
                      </div>
                    </div>
                  )}
                  {allEnts.length > highConf.length && (
                    <div>
                      <div className="text-gray-700 font-mono text-[9px] font-bold tracking-widest mb-2 flex items-center gap-2">
                        <div className="w-1 h-2.5 rounded-full bg-gray-700" />
                        ALL ENTITIES — {allEnts.length} total
                      </div>
                      <div className="space-y-1 max-h-64 overflow-y-auto">
                        {allEnts.filter(e => e.confidence < 0.6).map((e, i) => (
                          <EntityRow key={i} entity={e} onInvestigate={investigate} />
                        ))}
                      </div>
                    </div>
                  )}
                  {allEnts.length === 0 && (
                    <div className="text-gray-700 font-mono text-xs text-center py-6 border border-dashed border-gray-800 rounded-xl">
                      No entities extracted from correlation results
                    </div>
                  )}
                </div>
              )}

              {/* Breaches */}
              {activeView === 'breaches' && (
                <div className="space-y-1.5 max-h-72 overflow-y-auto">
                  {result.breach_names.map((name, i) => (
                    <div key={i} className="flex items-center gap-3 px-3 py-2 bg-black/30 border border-gray-800/40 rounded-lg">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
                      <span className="font-mono text-xs text-gray-300 truncate">{name}</span>
                    </div>
                  ))}
                  {result.breach_names.length === 0 && (
                    <div className="text-gray-700 font-mono text-xs text-center py-6">No breaches found</div>
                  )}
                </div>
              )}

              {/* Queries */}
              {activeView === 'queries' && (
                <div className="space-y-3">
                  <div className="text-gray-600 font-mono text-[10px] leading-relaxed">
                    These queries were dispatched as multi-line LeakOSINT batch payloads.
                    Entries in quotes used exact-search (cost-optimised).
                  </div>
                  <div className="space-y-1 max-h-72 overflow-y-auto">
                    {result.queries_sent.map((q, i) => {
                      const isExact = q.startsWith('"')
                      return (
                        <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-black/30 border border-gray-800/30 rounded-lg">
                          <span className={`font-mono text-[8px] font-bold px-1.5 py-0.5 rounded border shrink-0 ${isExact ? 'text-green-500 bg-green-950/30 border-green-800/30' : 'text-gray-600 border-gray-700/30'}`}>
                            {isExact ? 'EXACT' : 'FUZZY'}
                          </span>
                          <span className="font-mono text-xs text-gray-400 truncate">{q}</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>

          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}
